import { useState, useEffect, useRef } from "react";
import { AlertTriangle, RefreshCw, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { useSMMGeneratorContext } from "../context/SMMGeneratorContext";


function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getScoreColor(score: number): string {
  if (score >= 0.7) return "text-emerald-500";
  if (score >= 0.5) return "text-amber-500";
  return "text-destructive";
}

function getScoreBarColor(score: number): string {
  if (score >= 0.7) return "bg-emerald-500";
  if (score >= 0.5) return "bg-amber-500";
  return "bg-destructive";
}

const AuditSkeleton = () => (
  <div className="p-3 space-y-3 animate-pulse">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="rounded-md border border-border p-2.5 space-y-2">
        <div className="flex items-center justify-between">
          <div className="h-3 bg-muted/40 rounded w-2/3" />
          <div className="h-3 bg-muted/40 rounded w-10" />
        </div>
        <div className="h-1 bg-muted/30 rounded-full w-full" />
        <div className="h-2.5 bg-muted/30 rounded w-4/5" />
      </div>
    ))}
  </div>
);

export const ManifestAuditPanel = () => {
    const { confidenceScores, previewEntry, previewConfidenceScores, editedContent, isDirty, scoresStale, isScoring, isGenerating, handleReScore, hasOpenAIKey } = useSMMGeneratorContext();

  // While previewing a history version, display that version's scores instead of the current ones.
  const activeScores = previewEntry ? previewConfidenceScores : confidenceScores;
  const [showRescoreHint, setShowRescoreHint] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isDirty && scoresStale) {
      setShowRescoreHint(true);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      hintTimerRef.current = setTimeout(() => setShowRescoreHint(false), 3000);
    } else {
      setShowRescoreHint(false);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    }
    return () => { if (hintTimerRef.current) clearTimeout(hintTimerRef.current); };
  }, [isDirty, scoresStale, editedContent]);

  if (isGenerating) {
    return <AuditSkeleton />;
  }

  if (isScoring) {
    return (
      <div className="p-3 space-y-3">
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-1.5 text-xs"
          disabled
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Re-evaluating...
        </Button>
        <div className="space-y-3 animate-pulse">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-md border border-border p-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <div className="h-3 bg-muted/40 rounded w-2/3" />
                <div className="h-3 bg-muted/40 rounded w-10" />
              </div>
              <div className="h-1 bg-muted/30 rounded-full w-full" />
              <div className="h-2.5 bg-muted/30 rounded w-4/5" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!activeScores) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
        <p className="text-xs text-muted-foreground">
          No audit data available. Save a manifest first.
        </p>
      </div>
    );
  }

  const sections = Object.entries(activeScores)
    .filter(([key, value]) => key !== "overall" && value?.score != null)
    .map(([key, section]) => ({
      key,
      label: humanize(key),
      score: section.score as number,
      note: section.note as string | undefined,
    }));

  const lowSections = sections.filter(({ score }) => score < 0.6);

  return (
    <div className="p-3 space-y-3">
      {isDirty && !previewEntry && (
        <Tooltip open={showRescoreHint || undefined}>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5 text-xs"
              onClick={handleReScore}
              disabled={!hasOpenAIKey || isScoring || isGenerating}
              title={!hasOpenAIKey ? "An OpenAI API key is required" : undefined}
            >
              {isScoring ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Re-evaluate
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className={showRescoreHint ? "font-medium" : ""}
          >
            {showRescoreHint ? "Re-evaluate to verify your changes" : "Re-evaluate confidence scores"}
          </TooltipContent>
        </Tooltip>
      )}

      {lowSections.length > 0 && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-600 dark:text-amber-400 space-y-1">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              {lowSections.length} section{lowSections.length > 1 ? "s" : ""} scored below 60%.
              This may affect tag generation and downstream coverage quality.
            </span>
          </div>
        </div>
      )}

      {sections.map(({ key, label, score, note }) => {
        const pct = Math.round(score * 100);
        const isComplete = score >= 0.7;
        return (
          <div
            key={key}
            className="rounded-md border border-border p-2.5 space-y-1.5"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-foreground leading-snug">{label}</span>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className={cn("text-[11px] font-semibold tabular-nums", getScoreColor(score))}>
                  {pct}%
                </span>
                <span
                  className={cn(
                    "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold",
                    isComplete
                      ? "bg-emerald-500/15 text-emerald-500"
                      : "bg-destructive/15 text-destructive"
                  )}
                >
                  {isComplete ? (
                    <>
                      <CheckCircle2 className="h-2.5 w-2.5" />
                      Complete
                    </>
                  ) : (
                    <>
                      <XCircle className="h-2.5 w-2.5" />
                      Gap
                    </>
                  )}
                </span>
              </div>
            </div>
            <div className="w-full h-1 rounded-full bg-muted overflow-hidden">
              <div
                className={cn("h-full rounded-full transition-all", getScoreBarColor(score))}
                style={{ width: `${pct}%` }}
              />
            </div>
            {note && (
              <p className="text-[11px] text-muted-foreground leading-snug">{note}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};
